<script setup>
// 旋转图片工具（slug rotate-image）。复刻 design "Rotate Image.html" 的 rotate 逻辑并扩展：
// 上传 → canvas 实时预览 → 90° 左右旋转 + 自由角度滑块 + 微调拉直（straighten）→ 自动重算画布边界 → 下载。
// 原 blueprint 的 rotate(dir) 仅做 90° 整步旋转（交换宽高 + ctx.rotate(dir*PI/2)）；这里在其基础上加入
// 任意角度旋转：旋转后用 |w·cosθ|+|h·sinθ| / |w·sinθ|+|h·cosθ| 重新计算外接盒，保证图像不被裁掉。
// 纯浏览器内运算（Canvas 2D / Image / FileReader / URL.createObjectURL），不上传、不依赖任何 npm 包。
import { ref, computed, watch, nextTick, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useAuthModals } from "@/composables/useAuthModals";
import { usePaywallModal } from "@/composables/usePaywallModal";
import { useBatch } from "@/composables/useBatch.js";
import { useCanvasDecode } from "@/composables/useCanvasDecode";
import { tierOf, fileTooLargeMsg, looksLikeEditableImage } from "@/settings/free-tools/batch-limits.js";

const props = defineProps({
  mode: { type: String, default: "rotate-image" }, // 单工具，mode 备用
  slug: { type: String, default: "" },
});

const store = useStore();
const { downloadBlob } = useFreeToolsDownload();
const { decodeForCanvas, decodeErrorMsg } = useCanvasDecode();

/* ---- 上传 / 画布状态 ---- */
const stage = ref("drop"); // drop | work
const dragging = ref(false);
const fileInput = ref(null);
const canvas = ref(null);
const fileName = ref("image.png");
const thumbUrl = ref("");

/* ---- 旋转控制 ---- */
const quarter = ref(0); // 90° 整步累计（度，恒为 90 的倍数）
const fine = ref(0); // 自由 / 拉直微调角度（度，-45 ~ 45）

// 源图绘制到离屏 base canvas，旋转始终基于原始像素，避免反复旋转累积模糊
let base = null; // HTMLCanvasElement，原图
let baseName = "image";
let srcType = "image/png";
let lastThumbUrl = "";

const totalAngle = computed(() => quarter.value + Number(fine.value)); // 总旋转角（度）
const dimReadout = ref("—");

/* ---- 批量模式（nuxt#21）：纯客户端 canvas，多文件 → 队列 ----
   旋转是纯前端零成本工具，批量走 client:true / category:image。
   批量只支持 90° 整步旋转（quarterB），自由角度/拉直 fine 仅单文件预览用 —— 一套设置应用到全部文件。 */
const batchMode = ref(false);
const quarterB = ref(0); // 批量共享的 90° 累计角（度，恒为 90 的倍数）
const tierB = computed(() => tierOf(!!store.state.user.token, store.state.user.userInfo));
const tokenB = computed(() => store.state.user.token || "");
const userIdB = computed(() => (store.state.user.userInfo && store.state.user.userInfo.user_info_id) || "");
const _auth = useAuthModals();
const _paywall = usePaywallModal();
const batch = useBatch({
  category: "image",
  client: true,
  tier: tierB,
  token: tokenB,
  userId: userIdB,
  clientConvert: (file) => convertFileBatch(file),
  buildQuery: () => "",
  onSignup: () => _auth.openSignInModal(),
  onUpgrade: () => _paywall.openPaywall(["DST", "PES", "EXP", "JEF", "VP3", "HUS", "XXX", "EMD"]),
});

function fmtType() {
  return srcType === "image/jpeg" ? "image/jpeg" : srcType === "image/webp" ? "image/webp" : "image/png";
}

function revokeThumb() {
  if (lastThumbUrl) { URL.revokeObjectURL(lastThumbUrl); lastThumbUrl = ""; }
}

/* ---- 旋转绘制：把 base 以 totalAngle 旋转，画布尺寸取旋转后外接矩形 ---- */
function paint() {
  const c = canvas.value;
  if (!c || !base) return;
  const w = base.width, h = base.height;
  const rad = (totalAngle.value * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  // 旋转后外接盒（自动重算边界）
  const nw = Math.max(1, Math.round(w * cos + h * sin));
  const nh = Math.max(1, Math.round(w * sin + h * cos));
  c.width = nw;
  c.height = nh;
  const ctx = c.getContext("2d");
  ctx.clearRect(0, 0, nw, nh);
  ctx.save();
  ctx.translate(nw / 2, nh / 2);
  ctx.rotate(rad);
  ctx.drawImage(base, -w / 2, -h / 2);
  ctx.restore();
  dimReadout.value = nw + " × " + nh + " px";
}

/* ---- 文件加载 ---- */
async function loadFile(file) {
  if (!file || !looksLikeEditableImage(file)) { alert("Please choose an image file."); return; }
  const _m = fileTooLargeMsg(file, "image");
  if (_m) { alert(_m); return; }
  // 浏览器解不了的源(heic/tiff/tga/dds/psd…)→ 后端转 png 再进 canvas(用户无感)。命名仍取原文件。
  let _f;
  try { _f = await decodeForCanvas(file); }
  catch (e) { alert(decodeErrorMsg(file)); return; }
  baseName = file.name.replace(/\.[^.]+$/, "") || "image";
  srcType = file.type;
  fileName.value = file.name;
  revokeThumb();
  const url = URL.createObjectURL(_f);
  lastThumbUrl = url;
  const im = new Image();
  im.onload = async () => {
    base = document.createElement("canvas");
    base.width = im.naturalWidth;
    base.height = im.naturalHeight;
    base.getContext("2d").drawImage(im, 0, 0);
    thumbUrl.value = url;
    stage.value = "work";
    quarter.value = 0;
    fine.value = 0;
    await nextTick();
    paint();
  };
  im.onerror = () => { alert("Could not load this image."); };
  im.src = url;
}

/* ---- 拖放 / 选择 ---- */
function pick() { fileInput.value && fileInput.value.click(); }
function onInput(e) { handleFiles(e.target.files); e.target.value = ""; } // 清空 value 允许 add more / 重选同名
function onDrop(e) {
  e.preventDefault();
  dragging.value = false;
  handleFiles(e.dataTransfer && e.dataTransfer.files);
}
function reset() {
  stage.value = "drop";
  base = null;
  thumbUrl.value = "";
  quarter.value = 0;
  fine.value = 0;
  if (fileInput.value) fileInput.value.value = "";
}

/* ---- 旋转动作（复刻原 rotate(dir)，dir=-1 左 / dir=1 右）---- */
function rotateQuarter(dir) {
  // 累计 90° 整步；规整到 [0,360)
  quarter.value = (((quarter.value + dir * 90) % 360) + 360) % 360;
  paint();
}
function resetRotation() {
  quarter.value = 0;
  fine.value = 0;
  paint();
}

// 滑块拖动实时重绘
watch(fine, () => { if (base) paint(); });

/* ---- 下载（复刻原 downloadBtn：当前画布 toBlob → a.click）---- */
function download() {
  const c = canvas.value;
  if (!base || !c) return;
  const tt = fmtType();
  const ext = tt === "image/jpeg" ? "jpg" : tt === "image/webp" ? "webp" : "png";
  c.toBlob((b) => { if (!b) return; downloadBlob(b, baseName + "-rotated." + ext); }, tt, 0.95);
}

/* ---- 批量单文件转换：镜像单文件 paint()+download() 的旋转逻辑，但输入任意 file，且只套 90° 整步（quarterB） ---- */
async function convertFileBatch(file) {
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = () => rej(new Error("Could not read image"));
      im.src = url;
    });
    const w = image.naturalWidth, h = image.naturalHeight;
    // 批量只支持 90° 整步：rad 为 quarterB（自由角度 fine 不参与批量）。
    const rad = (((quarterB.value % 360) + 360) % 360) * Math.PI / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    const nw = Math.max(1, Math.round(w * cos + h * sin));
    const nh = Math.max(1, Math.round(w * sin + h * cos));
    const c = document.createElement("canvas");
    c.width = nw;
    c.height = nh;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, nw, nh);
    ctx.save();
    ctx.translate(nw / 2, nh / 2);
    ctx.rotate(rad);
    ctx.drawImage(image, -w / 2, -h / 2);
    ctx.restore();
    // 输出格式：保留 jpeg/webp 源类型，其余（含透明 png/gif）输出 png，与单文件 fmtType() 一致。
    const mt = file.type || "";
    const tt = mt === "image/jpeg" ? "image/jpeg" : mt === "image/webp" ? "image/webp" : "image/png";
    const ext = tt === "image/jpeg" ? "jpg" : tt === "image/webp" ? "webp" : "png";
    const blob = await new Promise((r) => c.toBlob(r, tt, 0.95));
    if (!blob) throw new Error("Rotation failed");
    return { blob, ext };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/* ---- 文件入口分流：批量中→add more；多文件→进批量；单文件→原单文件流 ---- */
function handleFiles(files) {
  if (!files || !files.length) return;
  if (batchMode.value) { batch.addFiles(files); return; }
  if (files.length > 1) { enterBatch(files); return; }
  loadFile(files[0]);
}
function enterBatch(files) {
  batchMode.value = true;
  batch.clearAll();
  quarterB.value = 0; // 进批量重置共享旋转角
  batch.addFiles(files);
}
function addMoreFiles() { fileInput.value && fileInput.value.click(); }
// 批量共享角度改变 → 作废旧产物，重新转换才出新角度（避免下载到旧角度的文件）。
function rotateBatchQuarter(dir) {
  quarterB.value = (((quarterB.value + dir * 90) % 360) + 360) % 360;
}
// 队列清空 → 退出批量回到 dropzone。
watch(() => batch.items.value.length, (n) => {
  if (batchMode.value && n === 0) batchMode.value = false;
});
// 套餐变化（登录/升级后）→ 重算锁定。
watch([tierB], () => batch.reevaluate());

onBeforeUnmount(revokeThumb);
</script>

<template>
  <div class="rotate-tool">
    <input ref="fileInput" type="file" accept="image/*" multiple hidden @change="onInput" />

    <!-- 拖放区 -->
    <div
      v-show="stage === 'drop' && !batchMode"
      class="dz"
      :class="{ drag: dragging }"
      @click="pick"
      @dragenter.prevent="dragging = true"
      @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false"
      @drop="onDrop"
    >
      <div class="dz-ico">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 11-3-6.7M21 4v5h-5" /></svg>
      </div>
      <h3>Drop your image here</h3>
      <div class="hint">or click to browse — PNG, JPG, WEBP, GIF</div>
      <div class="formats"><span>PNG</span><span>JPG</span><span>WEBP</span><span>GIF</span></div>
      <div class="dz-or">Rotated right in your browser — never stored</div>
      <button class="btn btn-primary btn-lg" type="button" @click.stop="pick">Choose Image</button>
    </div>

    <!-- batch 多文件队列（nuxt#21）：拖入 >1 个文件时进入；共享旋转走 #params 插槽，只支持 90° 整步 -->
    <div v-if="batchMode" class="ws batch-ws">
      <BatchQueue :batch="batch" category="image" :client="true" :file-count-max="20" @add-more="addMoreFiles">
        <template #params>
          <div class="ctl">
            <label>Rotate all 90°</label>
            <div class="xform-grid">
              <button type="button" @click="rotateBatchQuarter(-1)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M3 12a9 9 0 103-6.7M3 4v5h5" /></svg>
                Rotate left 90°
              </button>
              <button type="button" @click="rotateBatchQuarter(1)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M21 12a9 9 0 11-3-6.7M21 4v5h-5" /></svg>
                Rotate right 90°
              </button>
            </div>
          </div>
          <div class="angle-readout">All files rotate {{ ((quarterB % 360 + 360) % 360).toFixed(0) }}°</div>
          <div style="font-size: 12.5px; color: var(--tx-faint); font-weight: 600">One set of settings applies to all files — batch rotates in 90° steps.</div>
        </template>
      </BatchQueue>
    </div>

    <!-- 工作区（单文件） -->
    <div v-if="stage === 'work' && !batchMode" class="ws">
      <div class="ws-grid">
        <div class="ws-preview">
          <canvas ref="canvas" class="ws-canvas" />
        </div>

        <div class="ws-panel">
          <div class="ws-file">
            <img class="thumb" :src="thumbUrl" alt="" />
            <div class="meta">
              <div class="fn">{{ fileName }}</div>
              <div class="fsz">{{ dimReadout }}</div>
            </div>
            <button class="rm" type="button" title="Remove" @click="reset">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg>
            </button>
          </div>

          <!-- 90° 整步旋转 -->
          <div class="ctl">
            <label>Rotate 90°</label>
            <div class="xform-grid">
              <button type="button" @click="rotateQuarter(-1)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M3 12a9 9 0 103-6.7M3 4v5h5" /></svg>
                Rotate left 90°
              </button>
              <button type="button" @click="rotateQuarter(1)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M21 12a9 9 0 11-3-6.7M21 4v5h-5" /></svg>
                Rotate right 90°
              </button>
            </div>
          </div>

          <!-- 自由角度 / 拉直微调 -->
          <div class="ctl">
            <label>Straighten <span class="v">{{ Number(fine).toFixed(0) }}°</span></label>
            <input v-model.number="fine" type="range" min="-45" max="45" step="1" />
            <div class="ticks"><span>-45°</span><span>0°</span><span>+45°</span></div>
          </div>

          <div class="angle-readout">Total angle: {{ ((totalAngle % 360 + 360) % 360).toFixed(0) }}°</div>

          <div class="ws-actions">
            <button class="btn btn-primary btn-lg" type="button" @click="download">Apply &amp; Download</button>
            <button class="btn btn-outline" type="button" @click="resetRotation">Reset rotation</button>
            <button class="btn btn-outline" type="button" @click="reset">Rotate another image</button>
          </div>

          <p class="gen-note">Runs right in your browser — never stored.</p>
        </div>
      </div>
    </div>

    <div class="trust">
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></svg>Private — files stay on your device</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>Live canvas preview</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M7 12h10" /></svg>Unlimited &amp; free</span>
    </div>
  </div>
</template>

<style scoped>
.rotate-tool { width: 100%; }

/* dropzone */
.dz {
  border: 2px dashed var(--line-strong);
  border-radius: 20px;
  background: var(--surface);
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: 0.18s;
}
.dz.drag,
.dz:hover {
  border-color: #f97316;
  background: color-mix(in oklab, #f97316 6%, var(--surface));
}
.dz-ico { width: 56px; height: 56px; margin: 0 auto 14px; color: #f97316; }
.dz-ico svg { width: 100%; height: 100%; }
.dz h3 { font-family: var(--font-h); font-size: 20px; font-weight: 700; }
.dz .hint { color: var(--tx-dim); font-size: 14px; margin-top: 6px; }
.dz .formats { margin: 12px 0; display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; }
.dz .formats span { font-size: 12px; font-weight: 700; color: var(--tx-faint); border: 1px solid var(--line); border-radius: 7px; padding: 3px 10px; }
.dz-or { color: var(--tx-faint); font-size: 13px; margin: 10px 0 16px; }

/* workspace */
.ws-grid {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 22px;
  align-items: start;
  border: 1px solid var(--line);
  border-radius: 22px;
  background: var(--surface);
  overflow: hidden;
}
.ws-preview {
  display: grid;
  place-items: center;
  padding: 24px;
  min-height: 320px;
  min-width: 0;
  background: repeating-conic-gradient(#0c0e13 0 25%, #0a0b10 0 50%) 50% / 26px 26px;
}
.ws-canvas {
  max-width: 100%;
  max-height: 420px;
  height: auto;
  width: auto;
  border-radius: 10px;
  box-shadow: 0 18px 50px -18px rgba(0, 0, 0, 0.85);
}
.ws-panel {
  padding: 24px;
  border-left: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* file row */
.ws-file {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 10px 12px;
  background: var(--surface-2);
}
.ws-file .thumb { width: 40px; height: 40px; border-radius: 8px; object-fit: cover; flex: none; background: var(--surface); }
.ws-file .meta { flex: 1; min-width: 0; }
.ws-file .fn { font-weight: 600; font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ws-file .fsz { font-size: 12px; color: var(--tx-faint); }
.ws-file .rm { background: none; border: none; color: var(--tx-faint); cursor: pointer; flex: none; padding: 4px; }
.ws-file .rm:hover { color: var(--tx); }

/* controls */
.ctl label {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--tx-faint);
  display: block;
  margin-bottom: 9px;
}
.ctl label .v { color: #f4cd76; text-transform: none; letter-spacing: 0; font-weight: 700; }
.ctl input[type="range"] { width: 100%; accent-color: #f97316; }
.ticks { display: flex; justify-content: space-between; font-size: 11px; color: var(--tx-faint); font-weight: 600; margin-top: 4px; }

/* 90° transform buttons (复刻 design .xform-grid) */
.xform-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.xform-grid button {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 18px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: var(--surface-2);
  color: var(--tx);
  font-family: var(--font-b);
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: 0.15s;
}
.xform-grid button:hover {
  border-color: #f97316;
  background: color-mix(in oklab, #f97316 10%, var(--surface-2));
  transform: translateY(-2px);
}
.xform-grid button svg { width: 24px; height: 24px; color: #f97316; }

.angle-readout { font-size: 12.5px; color: var(--tx-faint); font-weight: 600; text-align: center; }

/* batch workspace：队列自身带边框/卡片，外层只做宽度容器 */
.batch-ws { width: 100%; }

.ws-actions { display: flex; flex-direction: column; gap: 10px; margin-top: 4px; }
.ws-actions .btn { white-space: normal; text-align: center; line-height: 1.25; }
.gen-note { font-size: 12.5px; color: var(--tx-faint); }

/* trust row */
.trust { display: flex; align-items: center; justify-content: center; gap: 30px; flex-wrap: wrap; margin: 30px auto 0; color: var(--tx-faint); font-size: 13.5px; font-weight: 600; }
.trust .i { display: flex; align-items: center; gap: 8px; }
.trust .i svg { width: 16px; height: 16px; color: #34d399; }

@media (max-width: 760px) {
  .ws-grid { grid-template-columns: 1fr; }
  .ws-panel { border-left: none; border-top: 1px solid var(--line); }
}
</style>
